import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button } from '@mui/material';
import type { InferType } from 'yup';

import { CommonError, handleError } from '@/utils/errorUtils';

import { FormikForm } from '@/components/formik/FormikForm';
import { FormikSubmit } from '@/components/formik/FormikSubmit';
import { GenericModalFooter } from '@/components/modal/GenericModalFooter';
import { PaymentMethod } from '@/components/paymentMethod/PaymentMethod';
import { usePaymentMethod } from '@/components/paymentMethod/usePaymentMethod';
import type { TStepComponentProps } from '@/components/stepper/types';
import { SubmitError } from '@/components/submitError/SubmitError';

import type { TOnSubmit } from '@/types/formTypes';
import type { TCreateStationForm } from '@/types/station';
import type { TAddPaymentMethodForm } from '@/types/wallet';

import { paymentMethodSchema } from '@/validation/wallet';

import { styles as formStyles } from '../Form.styles';

type TAddPaymentMethodData = InferType<typeof paymentMethodSchema>;

const prepareData = ({ paymentMethod }: Partial<TCreateStationForm>) =>
  paymentMethod != null ? { data: { paymentMethodId: String(paymentMethod.id) } } : {};

export const BankConnection = ({
  data,
  moveBack,
  onSubmit,
}: TStepComponentProps<TCreateStationForm>) => {
  const { t } = useTranslation();
  const { resolve } = usePaymentMethod();
  const [error, setError] = useState<string | null>(null);
  const initial = useMemo(() => prepareData(data), [data]);

  const handleSubmit: TOnSubmit<TAddPaymentMethodData> = async (values, { setSubmitting }) => {
    try {
      const method = await resolve(values as TAddPaymentMethodForm);

      await onSubmit({
        paymentMethod: {
          id: method.id,
          status: method.status,
          isDefault: true,
        },
      });
    } catch (err) {
      setSubmitting(false);

      if (err instanceof CommonError) {
        setError(t(err.messageKey));
      } else {
        setError(`${t('errors.unknown')}\n${t('errors.tryAgain')}`);
        handleError(err, 'UNHANDLED_RESPONSE');
      }
    }
  };

  return (
    <FormikForm
      initialValues={initial}
      validationSchema={paymentMethodSchema}
      onSubmit={handleSubmit}
      sx={formStyles.form}
    >
      <SubmitError error={error} dismiss={() => setError(null)} sx={formStyles.errorToast} />

      <Box sx={formStyles.content}>
        <PaymentMethod
          titles={{
            main: 'stationSetup.connectBank.title',
            selectExisting: 'stationSetup.connectBank.selectExisting',
            addFirst: 'stationSetup.connectBank.choosePayment',
            addNew: 'stationSetup.connectBank.addNew',
          }}
        />
      </Box>

      <GenericModalFooter sx={formStyles.footer} squareButtons>
        <Box sx={formStyles.controls}>
          <Button variant="outlined" onClick={moveBack}>
            {t('common.back')}
          </Button>

          <FormikSubmit>{t('common.next')}</FormikSubmit>
        </Box>
      </GenericModalFooter>
    </FormikForm>
  );
};
